import * as React from "react"
import { useEffect } from "react"
import { MapContainer, TileLayer, Marker, Polyline, Polygon, Tooltip, useMapEvents, useMap } from "react-leaflet"
import * as L from "leaflet"
import moment from "moment"

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlaceRecord {
    id: string
    name: string
    latitude: number
    longitude: number
    created?: string
    assignedTo?: string
    assignedToObj?: { firstName: string; lastName: string; color?: string }
    category?: { name: string; icon?: string }
    stage?: { name: string; icon?: string }
    placeStatus?: { name: string; color: string }
    vehicleVisit?: boolean | object
    vehicleLastVisit?: { visitDate?: string }
    creditCode?: string
}

interface VehicleRecord {
    id?: string
    latitud: number   // Spanish spelling — matches API field name
    longitud: number
    coche?: { marca?: string; modelo?: string; ficha?: string }
}

interface GeofenceRecord {
    id: string
    name: string
    placeId?: string
    centroidLatitude?: number
    centroidLongitude?: number
    pointsCount?: number
    coordinates: Array<{ lat: number; lng: number }>
}

interface Props {
    records: PlaceRecord[]
    vehicleRecords?: VehicleRecord[]
    geofences?: GeofenceRecord[]
    center: { lat: number; lng: number }
    zoom?: number
    mapMode?: string | null
    polygon?: { lat: number; lng: number }[]
    isLoading?: boolean
    isMobile?: boolean
    totalRecordsWithCoords?: number
    requestSize?: string
    onMarkerClick?: (record: PlaceRecord) => void
    onVehicleClick?: (record: VehicleRecord) => void
    onGeofenceClick?: (record: GeofenceRecord) => void
    onMapClick?: (lat: number, lng: number) => void
    style?: React.CSSProperties
}

// ─── Color helpers ─────────────────────────────────────────────────────────────

const getPinColor = (record: PlaceRecord): string => {
    if (record.assignedTo && record.assignedToObj?.color) return record.assignedToObj.color
    const diff = moment().diff(moment(record.created), "days")
    if (diff < 14) return "#7ac2ff"
    if (diff < 30) return "#034986"
    if (diff < 60) return "#222bd9"
    return "#7a7a7a"
}

const getVehicleVisitColor = (record: PlaceRecord): string => {
    if (!record.vehicleLastVisit?.visitDate) return "#7a7a7a"
    const diff = moment().diff(moment(record.vehicleLastVisit.visitDate), "days")
    if (diff < 7) return "#7ac2ff"
    if (diff < 14) return "#034986"
    if (diff < 30) return "#002f58"
    return "#333333"
}

// ─── Icon factories ────────────────────────────────────────────────────────────

// Place pin: SVG with initials + status dot (top-right) + vehicle visit dot (top-left)
const createPlacePinIcon = (record: PlaceRecord): L.DivIcon => {
    const color = getPinColor(record)
    const initials = record.assignedTo && record.assignedToObj
        ? record.assignedToObj.firstName.substring(0, 1).toUpperCase()
        : ""

    const statusDot = record.placeStatus?.color
        ? `<span style="position:absolute;top:-5px;right:-7px;width:13px;height:13px;
            border-radius:50%;background-color:${record.placeStatus.color};
            border:2px solid #fff;display:block;"></span>`
        : ""

    const vehicleDot = record.vehicleVisit
        ? `<span style="position:absolute;top:-5px;left:-7px;width:13px;height:13px;
            border-radius:50%;background-color:${getVehicleVisitColor(record)};
            border:2px solid #fff;display:flex;align-items:center;justify-content:center;
            font-size:8px;color:#fff;font-weight:bold;">&#10005;</span>`
        : ""

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 40" width="28" height="40">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26S28 24.5 28 14C28 6.27 21.73 0 14 0z"
              fill="${color}" stroke="white" stroke-width="1.5"/>
        <circle cx="14" cy="14" r="7" fill="white" opacity="0.9"/>
        <text x="14" y="18.5" text-anchor="middle" font-size="9" font-weight="bold"
              fill="${color}" font-family="Arial,sans-serif">${initials}</text>
    </svg>`

    return L.divIcon({
        html: `<div style="position:relative;display:inline-block;width:28px;height:40px;">${svg}${statusDot}${vehicleDot}</div>`,
        className: "",
        iconSize: [28, 40],
        iconAnchor: [14, 40],
        popupAnchor: [0, -40],
    })
}

// Vehicle pin: orange circle, matches Google Maps PinComponent Type="vehicle"
const vehiclePinIcon = L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
        <circle cx="16" cy="16" r="15" fill="orange" stroke="white" stroke-width="2"/>
        <text x="16" y="20" text-anchor="middle" font-size="11" fill="white"
              font-weight="bold" font-family="Arial,sans-serif">VH</text>
    </svg>`,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
})

const geofenceLabelIcon = (name: string, pointsCount?: number): L.DivIcon => {
    const label = `<div style="
        display:inline-flex;
        align-items:center;
        gap:6px;
        padding:6px 11px;
        border-radius:10px;
        background:#ff7a00;
        color:#fff;
        border:2px solid rgba(255,255,255,0.9);
        box-shadow:0 3px 10px rgba(0,0,0,0.45);
        font-size:12px;
        font-weight:700;
        white-space:nowrap;
        line-height:1.2;
        letter-spacing:0.01em;
    ">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)" style="flex:0 0 auto;">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        <span>${name}</span>
    </div>
    <div style="
        width:0;
        height:0;
        border-left:7px solid transparent;
        border-right:7px solid transparent;
        border-top:8px solid #ff7a00;
        margin:0 auto;
        filter:drop-shadow(0 2px 2px rgba(0,0,0,0.3));
    "></div>`

    return L.divIcon({
        html: label,
        className: "",
        iconSize: [180, 44],
        iconAnchor: [90, 44],
    })
}

// ─── Inner components ──────────────────────────────────────────────────────────

const InvalidateSize: React.FC = () => {
    const map = useMap()
    useEffect(() => {
        const t1 = setTimeout(() => map.invalidateSize(), 100)
        const t2 = setTimeout(() => map.invalidateSize(), 400)
        const t3 = setTimeout(() => map.invalidateSize(), 1000)
        const container = map.getContainer()
        let ro: ResizeObserver | null = null
        if (typeof ResizeObserver !== "undefined" && container) {
            ro = new ResizeObserver(() => map.invalidateSize())
            ro.observe(container)
        }
        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); ro?.disconnect()
        }
    }, [map])
    return null
}

const MapClickHandler: React.FC<{ onClick?: (lat: number, lng: number) => void }> = ({ onClick }) => {
    useMapEvents({ click(e) { onClick?.(e.latlng.lat, e.latlng.lng) } })
    return null
}

// ─── Main component ────────────────────────────────────────────────────────────

const LeafletMainMap: React.FC<Props> = ({
    records,
    vehicleRecords = [],
    geofences = [],
    center,
    zoom = 10,
    mapMode,
    polygon = [],
    isLoading = false,
    isMobile = false,
    totalRecordsWithCoords,
    requestSize,
    onMarkerClick,
    onVehicleClick,
    onGeofenceClick,
    onMapClick,
    style,
}) => {
    const validRecords = records.filter(r => r.latitude && r.longitude)
    const validVehicles = vehicleRecords
        .filter(v => v.latitud > 0 && v.longitud > 0)
        .slice(0, isMobile ? 20 : vehicleRecords.length)

    const safeCenter: [number, number] = (center?.lat && center?.lng)
        ? [center.lat, center.lng]
        : [18.479777, -69.9341112]

    const polygonLatLngs: [number, number][] = polygon.map(p => [p.lat, p.lng])
    const locationsCount = totalRecordsWithCoords ?? validRecords.length

    return (
        <div style={{
            position: "relative",
            zIndex: 1,
            ...(style ?? { width: "100%", height: "100%" })
        }}>
            <MapContainer
                center={safeCenter}
                zoom={zoom}
                style={{ width: "100%", height: "100%", position: "relative" }}
                scrollWheelZoom={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <InvalidateSize />
                <MapClickHandler onClick={onMapClick} />

                {geofences
                    .filter(geofence => geofence.coordinates.length >= 3)
                    .map(geofence => (
                        <React.Fragment key={geofence.id}>
                            <Polygon
                                positions={geofence.coordinates.map(point => [point.lat, point.lng] as [number, number])}
                                pathOptions={{
                                    color: "#ffffff",
                                    weight: 8,
                                    opacity: 0.95,
                                    fillOpacity: 0,
                                }}
                                eventHandlers={{ click: () => onGeofenceClick?.(geofence) }}
                            />

                            <Polygon
                                positions={geofence.coordinates.map(point => [point.lat, point.lng] as [number, number])}
                                pathOptions={{
                                    color: "#ff7a00",
                                    weight: 4,
                                    opacity: 1,
                                    fillColor: "#ff9f43",
                                    fillOpacity: 0.24,
                                    dashArray: "10 6",
                                }}
                                eventHandlers={{ click: () => onGeofenceClick?.(geofence) }}
                            >
                                <Tooltip sticky direction="top">
                                    {`${geofence.name}${geofence.pointsCount ? ` • ${geofence.pointsCount} pts` : ""}`}
                                </Tooltip>
                            </Polygon>

                            {geofence.centroidLatitude && geofence.centroidLongitude && (
                                <Marker
                                    position={[geofence.centroidLatitude, geofence.centroidLongitude]}
                                    icon={geofenceLabelIcon(geofence.name, geofence.pointsCount)}
                                    eventHandlers={{ click: () => onGeofenceClick?.(geofence) }}
                                />
                            )}
                        </React.Fragment>
                    ))}

                {/* Place markers */}
                {validRecords.map(record => (
                    <Marker
                        key={record.id}
                        position={[record.latitude, record.longitude]}
                        icon={createPlacePinIcon(record)}
                        eventHandlers={{ click: () => onMarkerClick?.(record) }}
                    />
                ))}

                {/* Vehicle markers */}
                {validVehicles.map((vehicle, index) => (
                    <Marker
                        key={`vehicle-${vehicle.id ?? index}`}
                        position={[vehicle.latitud, vehicle.longitud]}
                        icon={vehiclePinIcon}
                        eventHandlers={{ click: () => onVehicleClick?.(vehicle) }}
                    />
                ))}

                {/* Polygon drawing — dashed blue line connecting clicked points */}
                {mapMode === "drawPolygon" && polygonLatLngs.length > 1 && (
                    <Polyline
                        positions={polygonLatLngs}
                        pathOptions={{ color: "#1890ff", weight: 2, dashArray: "6 4" }}
                    />
                )}
            </MapContainer>

            {/* Location counter overlay — matches Google Maps version */}
            {!isLoading && locationsCount > 0 && (
                <div style={{
                    position: "absolute", top: 10, left: 10, zIndex: 1000,
                    background: "rgba(255,255,255,0.95)", color: "#333",
                    padding: "10px 15px", borderRadius: 8, fontSize: 13,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    border: "1px solid rgba(0,0,0,0.1)", pointerEvents: "none",
                }}>
                    <div className="d-flex align-items-center">
                        <i className="fa fa-map-marker-alt text-primary me-2"></i>
                        <div>
                            <strong>{locationsCount}</strong> ubicaciones
                            {process.env.NODE_ENV === "development" && requestSize && (
                                <small className="text-muted d-block" style={{ fontSize: 11 }}>
                                    {requestSize}
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile performance overlay — matches Google Maps version */}
            {isMobile && validRecords.length > 50 && (
                <div style={{
                    position: "absolute", top: 10, right: 10, zIndex: 1000,
                    background: "rgba(0,0,0,0.7)", color: "white",
                    padding: "8px 12px", borderRadius: 4, fontSize: 12,
                    pointerEvents: "none",
                }}>
                    Mostrando {Math.min(validRecords.length, 50)} de {validRecords.length} ubicaciones
                </div>
            )}

            {/* Polygon drawing mode hint */}
            {mapMode === "drawPolygon" && (
                <div style={{
                    position: "absolute", bottom: 16, left: "50%",
                    transform: "translateX(-50%)", zIndex: 1000,
                    background: "rgba(24,144,255,0.9)", color: "white",
                    padding: "6px 14px", borderRadius: 20, fontSize: 12,
                    pointerEvents: "none", whiteSpace: "nowrap",
                }}>
                    <i className="fa fa-draw-polygon me-2"></i>
                    Haz clic en el mapa para dibujar el polígono
                </div>
            )}
        </div>
    )
}

export default LeafletMainMap
